import { qs, clear, el } from "../utils/dom";

export function mountProgress() {
    const host = qs("#progress");
    clear(host);
    const bar = el("div", { class: "progress-bar" }, el("div", { class: "progress-inner", id: "progress-inner" }));
    const label = el("div", { class: "progress-label", id: "progress-label" }, "Idle");
    host.append(bar, label);
}

export function progressUpdate(label: string, pct: number) {
    const inner = document.getElementById("progress-inner") as HTMLDivElement | null;
    const lab = document.getElementById("progress-label") as HTMLDivElement | null;
    if (inner) inner.style.width = `${Math.max(0, Math.min(100, pct))}%`;
    if (lab) lab.textContent = label;
}

// Enhanced progress messages based on stage
const PROGRESS_MESSAGES = [
    { pct: 10, msg: "Initializing random number generator..." },
    { pct: 20, msg: "Validating constraints and checking feasibility..." },
    { pct: 30, msg: "Placing must-together constraint chains..." },
    { pct: 40, msg: "Computing affinity scores and bias weights..." },
    { pct: 50, msg: "Distributing students across groups..." },
    { pct: 60, msg: "Balancing tags for diversity (pass 1/3)..." },
    { pct: 70, msg: "Balancing skill levels (pass 2/3)..." },
    { pct: 80, msg: "Final optimization pass (3/3)..." },
    { pct: 90, msg: "Applying leftover policy and repairs..." },
    { pct: 95, msg: "Verifying constraint satisfaction..." },
    { pct: 100, msg: "Done!" }
];

export async function runWithMinDuration<T>(
    task: (tick: (label: string, p: number) => void) => Promise<T> | T,
    durationMs: number
): Promise<T> {
    const startTime = performance.now();
    const updates: { label: string; pct: number; time: number }[] = [];

    // Collect all progress updates from the actual task
    const result = await task((label, pct) => {
        updates.push({ label, pct, time: performance.now() - startTime });
    });

    const actualDuration = performance.now() - startTime;

    // If we want longer animation, replay the updates stretched over the desired duration
    if (durationMs > actualDuration) {
        const animStart = performance.now();

        // Smooth animation loop
        const animate = async () => {
            const elapsed = performance.now() - animStart;
            const progress = Math.min(100, (elapsed / durationMs) * 100);

            // Find the appropriate message based on current progress
            const currentStage = PROGRESS_MESSAGES.find(m => progress < m.pct)
                || PROGRESS_MESSAGES[PROGRESS_MESSAGES.length - 1];

            if (currentStage) {
                progressUpdate(currentStage.msg, progress);
            }

            if (elapsed < durationMs) {
                await new Promise(resolve => requestAnimationFrame(() => resolve(undefined)));
                await animate();
            } else {
                progressUpdate("Done!", 100);
            }
        };

        await animate();
    } else {
        // If actual execution was slower than desired, just replay the real updates
        for (const update of updates) {
            progressUpdate(update.label, update.pct);
        }
    }

    return result;
}
